"use client"
import {useAuth} from "../../app/providers/auth-provider";
import PageLoader from "../Loader/pageLoader";
import {useEffect, useRef, useState} from "react";
import ThemeModifier from "../Layout/ThemeModifier";
import UserDealNavbar from "./DealNavBar";
import Login from "../Auth/Login";
import Footer from "../Layout/Footer";
import ScrollUp from "../ScrollUp/ScrollUp";
import {isPostHogActive} from "../../utils";
import {useConfig} from "../ConfigProvider";
import {usePostHog} from "posthog-js/react";
import React from "react";
import { FiltersProvider } from "app/providers/filters-provider";

export default function DealsLayout(
    {
        siteConfig,
        topRatedProducts,
        categories,
        initialSearch,
        utmSource,
        pageConfig,
        children,
        userType = null // 'user' or 'creator'
    }) {

    const PAGE_SIZE = 40;

    const {user} = useAuth() || {};
    const config = useConfig()
    const posthog = usePostHog();

    const [inputValue, setInputValue] = useState(initialSearch);
    const [refresh, setRefresh] = useState(false);
    const [deals, setDeals] = useState([]);
    const [totalActivePromotions, setTotalActivePromotions] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [resultType, setResultType] = useState("filter")
    const [page, setPage] = useState(1);
    const isFetchingMore = useRef(false);
    const [isDealLoading, setDealLoading] = useState(false);

    const fetchDeals = async (search, categories = [], shoppingAudience, isPrime, page = 1, filters = {}) => {
        if (!config) return;
        categories = Array.isArray(categories) ? categories : [categories];

        if (!search && page === 1 && !Object.keys(filters).find(k => filters[k]) && (categories[0] === '' || categories[0] === 'Featured Products')) {
            setDeals(topRatedProducts?.data || []);
            setTotalActivePromotions(topRatedProducts?.totalActivePromotionsCount || 0);
            setTotalCount(topRatedProducts?.totalCount || topRatedProducts?.data?.length || 0);
            setResultType("filter");
            return;
        }

        if (isFetchingMore.current) {
            return;
        }

        isFetchingMore.current = true;

        let parsedResponse = {data: []};
        try {
            let url = `${config.siteApi.apiUrl}/crm/deals`;
            const postHogId = posthog.get_distinct_id();
            const queryParams = new URLSearchParams();

            if (search && search.length) {
                queryParams.append('search', search);
                if (utmSource && search === initialSearch) {
                    queryParams.append('utmSource', utmSource);
                }

                if (isPostHogActive()) {
                    posthog.capture("search", {"keyword": search});
                }
            }

            // categories are now collections
            if (categories?.length) {
                queryParams.append('collections', categories.join(','));
            }

            if (shoppingAudience) {
                queryParams.append('shoppingAudience', shoppingAudience);
            }

            if (isPrime) {
                queryParams.append('isPrime', isPrime);
            }

            // Add new filter format
            if (filters) {
                // Handle rating stars
                if (filters.ratingStars) {
                    queryParams.append('ratingStars', `gte:${filters.ratingStars}`);
                }

                // Handle rating count
                if (filters.ratingCount) {
                    queryParams.append('ratingCount', `gte:${filters.ratingCount}`);
                }

                // Handle price range
                if (filters.price) {
                    queryParams.append('price', `lte:${filters.price}`);
                }

                // Handle discount
                if (filters.discount) {
                    queryParams.append('discount', `gte:${filters.discount}`);
                }

                // Handle sort parameters
                if (filters.sort) {
                    queryParams.append('sort', filters.sort);
                }
            }

            queryParams.append('offset', (page - 1) * PAGE_SIZE);
            queryParams.append('limit', PAGE_SIZE);
            setDealLoading(true);

            const response = await fetch(`${url}?${queryParams}`, {
                method: "GET",
                headers: {
                    'x-poln-user-id': postHogId
                },
            });

            if (!response.ok) {
                throw new Error();
            }

            parsedResponse = await response.json();

            if (totalCount !== parsedResponse.totalCount) {
                setTotalCount(parsedResponse.totalCount)
            }
            if (page > 1) {
                setDeals(prev => [...prev, ...parsedResponse.data]);
            } else {
                setDeals(parsedResponse.data);
            }

            setResultType(parsedResponse.resultType)
            setTotalActivePromotions(parsedResponse.totalActivePromotionsCount)
        } catch (error) {
            setDeals([]);
            setTotalCount(0);
            setTotalActivePromotions(0);
        } finally {
            setDealLoading(false);
            isFetchingMore.current = false;
        }

        return parsedResponse.data;
    };

    const handleViewMore = (filters = {}, category) => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchDeals(inputValue, category, null, null, nextPage, filters);
    };

    // Determine whether to show Login component based on userType
    const shouldShowLogin = userType === 'creator' && !user?.email;

    return (
        <FiltersProvider>
            <div>
                <div className="light-theme-background"
                    style={{display: 'flex', flexDirection: 'column', minHeight: '100vh'}}>
                    <ThemeModifier siteConfig={siteConfig}/>
                    <div className="search-header-section" style={{padding: 0}}>
                        <UserDealNavbar
                        initialSearch={inputValue}
                        siteConfig={siteConfig}
                        userType={userType}
                        setTotalCount={setTotalCount}
                        setDeals={setDeals}
                        setPage={setPage}
                        setResultType={setResultType}
                        categories={categories}
                        fetchDeals={fetchDeals}
                        setInputValue={setInputValue}
                        />

                        {React.Children.map(children, child => 
                            React.cloneElement(child, {
                                siteConfig,
                                categories,
                                initialSearch,
                                user,
                                userType,
                                refresh,
                                inputValue,
                                handleViewMore,
                                deals,
                                setDeals,
                                totalActivePromotions,
                                totalCount,
                                resultType,
                                page,
                                setPage,
                                PAGE_SIZE,
                                isDealLoading,
                                setDealLoading,
                                fetchDeals,
                            })
                        )}
                        {shouldShowLogin && <Login/>}
                        <Footer name="deals" siteConfig={siteConfig}/>
                        <ScrollUp/>
                    </div>
                </div>
            </div>
        </FiltersProvider>
    )
} 